'use client';

// The sidebar's top row: the brand lockup plus the collapse (rail) or close
// (drawer) control. Split out of Sidebar.tsx (BL-2422).

import { clsx } from 'clsx';
import { PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { AuctogonLogo } from '@auctogon/ui/web';
import Button from '../ui/Button';
import type { SidebarVariant } from './nav-config';
import { SHELL_ROW_HEIGHT } from '../shell-geometry';
import { DARK_BAR_ICON_BUTTON, DARK_BAR_LEADING_PULL } from './dark-bar-icon-button';

/** Props for {@link SidebarHeader}. */
export interface SidebarHeaderProps {
  variant: SidebarVariant;
  isCollapsed: boolean;
  /** Collapse/expand in `rail`; close the drawer in `drawer`. */
  onToggle: () => void;
}

/**
 * The bordered box that holds the mark against the dark teal rail — COLLAPSED
 * rail only.
 *
 * Kept from the pre-BL-1127 header because at 64px it does real work: a lone
 * mark floating on the rail reads as debris, and the box doubles as the
 * hit-target outline for the expand control. The EXPANDED header dropped it in
 * BUG-1213 — see `RAIL_LOCKUP_HEIGHT`.
 */
const MARK_BOX_CLASSES =
  'w-7 h-7 bg-sidebar rounded-lg flex items-center justify-center border border-sidebar-border flex-shrink-0';

/** Mark height inside the collapsed rail's 28px bordered box, in px. */
const RAIL_COLLAPSED_MARK_HEIGHT = 17;

/**
 * Expanded-rail lockup height, in px — the full `lockup` artwork (mark + gap +
 * wordmark), not a composed pair.
 *
 * BUG-1213: the expanded header used to draw the mark and wordmark as separate
 * flex children with a 28px bordered box between them. That cost width twice
 * over — the box itself, plus a second 12px gap — and width is the only thing
 * that buys wordmark size here, because the wordmark renders ~9× as wide as it
 * is tall. The result was a 17px mark and a ~10.9px cap height, small enough
 * that Syne ExtraBold's counters (the enclosed space in `a`/`o`/`g`) fill in and
 * the word reads as a dense block. The 28px box next to it made the comparison
 * worse.
 *
 * Dropping the box in this state and using the one-piece variant buys back both
 * the box and a gap, and the artwork's own coordinates carry the mark/wordmark
 * baseline alignment — so the `AUCTOGON_LOCKUP_PROPORTIONS`-derived wordmark
 * height and the `translateY` baseline nudge this file used to compute are gone
 * with it. Off-brand drift is now structurally impossible rather than pinned by
 * arithmetic.
 *
 * 24px is the largest the 256px rail can carry: 24px left padding + 184.02px of
 * lockup (24 × the 7.6675 aspect ratio) + 28px chevron button + 12px right
 * padding = 248.02, leaving ~8px of breathing room that `mr-auto` absorbs.
 * `Sidebar.brand-lockup.integration.test.tsx` pins that sum.
 */
const RAIL_LOCKUP_HEIGHT = 24;

/**
 * Drawer-header lockup height, in px — the SMALLER of the two phone lockups,
 * and small on purpose.
 *
 * The drawer and `MobileNavBar` are the same chrome seen from either side of a
 * tap, so the lockup must not change size across it. 20px is `MobileNavBar`'s
 * height, and the smaller of the pair wins for a reason arithmetic settles: the
 * drawer is 256px wide (`max-w-nav-drawer`), and at the rail's 24px the lockup
 * is 184.02px, which with a 16px gutter each side and the 44px close button
 * needs 260px before any gap — so the wordmark ran under the button, which is
 * exactly what it did. At 20px the lockup is 153.35px. With the close button
 * first (BL-2553: pulled 10px into the gutter, then a 12px gap) the row totals
 * 16 + 34 + 12 + 153.35 + 16 = 231.35, leaving ~25px. Widening the drawer instead was the alternative and is
 * worse: the scrim is what dismisses it, and on a 360px phone a wider drawer
 * eats the tap target that closes it.
 */
const DRAWER_LOCKUP_HEIGHT = 20;

/**
 * The sidebar header for the given surface.
 *
 * @param props - see {@link SidebarHeaderProps}
 * @returns The header row.
 */
export default function SidebarHeader({ variant, isCollapsed, onToggle }: SidebarHeaderProps) {
  // In the drawer the lockup is the same, but the trailing control is a CLOSE,
  // not a collapse: the drawer has no icon tier to collapse to, so the rail's
  // chevron would be a control that does nothing.
  if (variant === 'drawer') {
    // BL-2553: 56px, the shared phone header row, so the drawer header and the
    // bar it opens from stay one geometry (BL-1648). The desktop rail keeps 64.
    return (
      <div className={clsx(SHELL_ROW_HEIGHT, 'flex items-center border-b border-sidebar-border')}>
        {/* 16px both sides, matching `MobileNavBar`'s gutter and `<main>`'s.
            BL-2553 (AC 3): the close button sits first, where the hamburger
            sits in the bar, with the same pull-in and gap, so the lockup stays
            at the bar's x across the tap and one spot both opens and closes.
            The lockup stays `flex-shrink-0` deliberately: it is a fixed-height
            SVG, so shrinking it would letterbox the artwork rather than scale
            it. The row is sized to fit by the arithmetic on
            DRAWER_LOCKUP_HEIGHT — narrowing the drawer means redoing that sum,
            not silently squashing the mark. */}
        <div className="flex w-full items-center gap-density-control px-4">
          <Button
            variant="ondark"
            size="icon"
            onClick={onToggle}
            title="Close navigation"
            aria-label="Close navigation"
            // The hamburger's classes: these two ARE one control seen from
            // either side of a tap (44px floor, chrome-less at rest).
            className={clsx(DARK_BAR_LEADING_PULL, DARK_BAR_ICON_BUTTON)}
          >
            {/* 24px, matching `MobileNavBar`'s hamburger. */}
            <X className="h-6 w-6" />
          </Button>
          <AuctogonLogo
            variant="lockup"
            height={DRAWER_LOCKUP_HEIGHT}
            title="Auctogon"
            className="text-secondary flex-shrink-0"
          />
        </div>
      </div>
    );
  }
  if (isCollapsed) {
    // The mark is the brand's only presence at 64px, so it takes the row the
    // chevron used to own — and the whole row stays the expand button, with the
    // chevron revealed on hover/focus so the affordance survives (BL-1127 AC 2).
    return (
      <div className="flex h-16 items-center border-b border-sidebar-border">
        {/* eslint-disable-next-line designsystem/no-raw-button -- non-CTA nav-rail chrome: the expand toggle is the rail logo itself */}
        <button
          type="button"
          data-testid="sidebar-toggle"
          onClick={onToggle}
          title="Expand sidebar"
          aria-label="Expand sidebar"
          className="group w-full h-full flex items-center justify-center text-sidebar-text/70 hover:text-sidebar-text transition-colors"
        >
          <span className={MARK_BOX_CLASSES}>
            <AuctogonLogo
              variant="mark"
              height={RAIL_COLLAPSED_MARK_HEIGHT}
              title="Auctogon"
              className="text-secondary group-hover:hidden group-focus-visible:hidden"
            />
            <PanelLeftOpen className="w-5 h-5 hidden group-hover:block group-focus-visible:block" />
          </span>
        </button>
      </div>
    );
  }
  return (
    <div className="flex h-16 items-center border-b border-sidebar-border">
      <div className="flex items-center pl-6 pr-3 w-full">
        {/* The one-piece lockup, NOT a composed mark + wordmark pair: the
            artwork's own coordinates carry the brand proportion and the shared
            baseline, so neither can drift, and dropping the bordered box that
            used to sit between them buys the width that makes the wordmark
            legible at rail size (BUG-1213). The box stays in the collapsed
            state, where it's the expand control's hit-target outline. */}
        {/* flex-shrink-0 is load-bearing, not decoration: as a shrinkable flex
            child the lockup collapses to ZERO width for the duration of the
            rail's 300ms expand/collapse transition (measured — the row is over
            budget while the rail is still animating out from 64px), so the logo
            visibly implodes and snaps back on every toggle. Pinning it lets the
            rail's edge clip the artwork instead, which is what a sidebar should
            do — and it means no layout constraint can ever squash the wordmark. */}
        <AuctogonLogo
          variant="lockup"
          height={RAIL_LOCKUP_HEIGHT}
          title="Auctogon"
          className="text-secondary mr-auto flex-shrink-0"
        />
        {/* eslint-disable-next-line designsystem/no-raw-button -- non-CTA nav-rail chrome: the collapse toggle on the dark rail, no classic variant fits */}
        <button
          type="button"
          onClick={onToggle}
          title="Collapse sidebar"
          aria-label="Collapse sidebar"
          className="text-sidebar-text/70 hover:text-sidebar-text transition-colors p-1 rounded"
        >
          <PanelLeftClose className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
